import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Upload, Plus, Trash2, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import DateInput from '../ui/DateInput';

// ClosuresManager — admin surface for spec §15 holiday calendar.
//
// Three sources, three actions:
//   federal_holiday → "Refresh now" button (idempotent annual sync)
//   doe_import      → "Upload iCal" → preview + curate + commit
//   manual          → add/remove ad-hoc closures inline
//
// Mounted as a new tab inside /admin/media.

const SOURCE_META = {
  federal_holiday: { label: 'Federal', cls: 'bg-blue-500/[0.1] border-blue-400/30 text-blue-300' },
  doe_import:      { label: 'NYC DOE',  cls: 'bg-[#B0FC38]/[0.1] border-[#B0FC38]/30 text-[#B0FC38]' },
  manual:          { label: 'Manual',   cls: 'bg-white/[0.04] border-white/[0.08] text-gray-400' },
};

export default function ClosuresManager() {
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');  // source filter
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/api/admin/media/closures${filter ? `?source=${filter}` : ''}`);
      setClosures(r?.closures || []);
    } catch (err) {
      setError(err?.message || 'Failed to load closures');
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const onRefreshFederal = async () => {
    setRefreshing(true);
    setFlash(null);
    try {
      const r = await api.post('/api/admin/media/closures/refresh-federal', {});
      setFlash({ tone: 'success', text: `Federal holidays refreshed: ${r.inserted} inserted, ${r.removed} removed (year ${r.year}).` });
      load();
    } catch (err) {
      setFlash({ tone: 'error', text: err instanceof ApiError ? err.message : 'Refresh failed' });
    } finally {
      setRefreshing(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Remove this closure? Federal/DOE entries return on next sync; manual entries are gone for good.')) return;
    try {
      await api.delete(`/api/admin/media/closures/${id}`);
      load();
    } catch (err) {
      setFlash({ tone: 'error', text: err instanceof ApiError ? err.message : 'Delete failed' });
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-[14px] font-semibold text-white tracking-tight">Closure calendar</h3>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Federal holidays + NYC DOE academic-calendar closures + manual blocks. Schools cannot book on closed dates.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={onRefreshFederal} disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-400/30 bg-blue-500/[0.08] text-[12px] text-blue-200 hover:bg-blue-500/[0.16] disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh federal
          </button>
          <button type="button" onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#B0FC38]/30 bg-[#B0FC38]/[0.08] text-[12px] text-[#B0FC38] hover:bg-[#B0FC38]/[0.16]">
            <Upload className="w-3.5 h-3.5" />
            Import DOE iCal
          </button>
          <button type="button" onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E]">
            <Plus className="w-3.5 h-3.5" />
            Manual block
          </button>
        </div>
      </div>

      {flash && (
        <div className={`mb-3 px-3 py-2 rounded-lg border text-[12px] flex items-start gap-2 ${
          flash.tone === 'success'
            ? 'border-[#B0FC38]/30 bg-[#B0FC38]/[0.06] text-[#B0FC38]'
            : 'border-red-400/30 bg-red-500/[0.06] text-red-300'}`}>
          {flash.tone === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5" />}
          <span className="flex-1">{flash.text}</span>
          <button onClick={() => setFlash(null)} className="opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <FilterPill active={!filter} onClick={() => setFilter('')}>All</FilterPill>
        {Object.entries(SOURCE_META).map(([k, m]) => (
          <FilterPill key={k} active={filter === k} onClick={() => setFilter(k)}>{m.label}</FilterPill>
        ))}
      </div>

      {error && <div className="mb-3 text-[12px] text-red-300">{error}</div>}

      {loading ? (
        <div className="text-[12px] text-gray-500">Loading…</div>
      ) : closures.length === 0 ? (
        <div className="text-[12px] text-gray-500 px-4 py-6 text-center rounded-lg border border-white/[0.04] bg-white/[0.02]">
          No closures yet. Refresh federal to seed the current year, or import the DOE iCal.
        </div>
      ) : (
        <div className="space-y-1">
          {closures.map((c) => {
            const meta = SOURCE_META[c.source] || SOURCE_META.manual;
            return (
              <div key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-white/[0.05] bg-white/[0.02]">
                <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                <span className="text-[13px] text-white tabular-nums shrink-0">{c.date}</span>
                <span className="text-[13px] text-gray-300 flex-1 truncate">{c.label}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${meta.cls}`}>
                  {meta.label}{c.closureYear ? ` · ${c.closureYear}` : ''}
                </span>
                <button onClick={() => onDelete(c.id)}
                  className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/[0.06] rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {importOpen && (
        <DoeImportDialog
          onClose={() => setImportOpen(false)}
          onCommitted={() => { setImportOpen(false); load(); setFlash({ tone: 'success', text: 'DOE iCal imported.' }); }}
        />
      )}
      {addOpen && (
        <ManualAddDialog
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] border ${
        active ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.08] text-[#B0FC38]'
               : 'border-white/[0.08] text-gray-400 hover:text-white hover:border-white/[0.15]'}`}>
      {children}
    </button>
  );
}

function DoeImportDialog({ onClose, onCommitted }) {
  const [icalText, setIcalText] = useState('');
  const [preview, setPreview] = useState(null);  // { entries, suggestedClosureYear }
  const [selected, setSelected] = useState(new Set());
  const [closureYear, setClosureYear] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onPreview = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await api.post('/api/admin/media/closures/import-doe', { ical: icalText });
      setPreview(r);
      setClosureYear(r.suggestedClosureYear || '');
      setSelected(new Set((r.entries || []).map((_, i) => i)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Preview failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onCommit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const chosen = preview.entries.filter((_, i) => selected.has(i));
      const r = await api.post('/api/admin/media/closures/import-doe', {
        entries: chosen,
        closureYear,
      });
      onCommitted?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Commit failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-2xl bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">Import DOE iCal</h2>
            <p className="text-[12px] text-gray-500 mt-0.5">
              Download the NYC DOE academic-calendar `.ics` file, then paste its contents below.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white">×</button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
          {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}

          {!preview ? (
            <form onSubmit={onPreview} className="space-y-3">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Paste iCal contents</span>
                <textarea required rows="12" value={icalText} onChange={(e) => setIcalText(e.target.value)}
                  placeholder="BEGIN:VCALENDAR…&#10;BEGIN:VEVENT&#10;…&#10;END:VEVENT&#10;…&#10;END:VCALENDAR"
                  className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[12px] font-mono" />
              </label>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
                <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
                  {submitting ? 'Parsing…' : 'Preview entries'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <label className="text-[12px] text-gray-400">
                  School year:
                  <input type="text" value={closureYear} onChange={(e) => setClosureYear(e.target.value)}
                    placeholder="2026-2027" className="ml-2 px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-white text-[12px] w-28" />
                </label>
                <span className="text-[11px] text-gray-500">
                  {selected.size} of {preview.entries.length} selected. Uncheck anything that isn't a NexSav off-day.
                </span>
              </div>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {preview.entries.map((e, i) => (
                  <label key={`${e.date}-${i}`} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-white/[0.05] bg-white/[0.02] cursor-pointer">
                    <input type="checkbox" checked={selected.has(i)}
                      onChange={(ev) => {
                        const next = new Set(selected);
                        if (ev.target.checked) next.add(i); else next.delete(i);
                        setSelected(next);
                      }}
                      className="w-3.5 h-3.5 accent-[#B0FC38]" />
                    <span className="text-[13px] text-white tabular-nums shrink-0">{e.date}</span>
                    <span className="text-[13px] text-gray-300 flex-1 truncate">{e.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/[0.05]">
                <button type="button" onClick={() => setPreview(null)}
                  className="px-3 py-2 text-[12px] text-gray-400 hover:text-white">← Back to paste</button>
                <button type="button" onClick={onCommit} disabled={submitting || selected.size === 0 || !closureYear}
                  className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-semibold hover:bg-[#9BE22E] disabled:opacity-50">
                  {submitting ? 'Importing…' : `Commit ${selected.size} closure${selected.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ManualAddDialog({ onClose, onAdded }) {
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/admin/media/closures', { date, label });
      onAdded?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-base font-semibold text-white tracking-tight">Add manual closure</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white">×</button>
        </div>
        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && <div className="text-[12px] text-red-300">{error}</div>}
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Date</span>
            <DateInput required value={date} onChange={setDate}
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px]" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Label</span>
            <input type="text" required value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="Team retreat, snow day, etc."
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px]" />
          </label>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
              {submitting ? 'Adding…' : 'Add closure'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
